"use client";

import { useReportWebVitals } from "next/web-vitals";
import { usePathname } from "next/navigation";

import { trackEvent } from "@/lib/analytics";

// Only the metrics worth acting on. Next also reports hydration and render
// timings, which would triple the event volume without changing any decision.
const REPORTED_METRICS = new Set(["INP", "LCP", "CLS", "TTFB", "FCP"]);

// CLS is unitless and lands around 0.01, so it is scaled to survive the integer
// rounding every other metric needs to keep payloads small.
const CLS_SCALE = 1000;

export function WebVitals() {
  const pathname = usePathname();

  useReportWebVitals((metric) => {
    if (!REPORTED_METRICS.has(metric.name)) {
      return;
    }

    if (typeof metric.value !== "number" || !Number.isFinite(metric.value)) {
      return;
    }

    const isCls = metric.name === "CLS";

    trackEvent("web_vital", {
      metric_name: metric.name,
      metric_value: Math.round(metric.value * (isCls ? CLS_SCALE : 1)),
      metric_unit: isCls ? "score_x1000" : "ms",
      metric_rating: getRating(metric),
      metric_id: metric.id,
      pathname,
    });
  });

  return null;
}

// `rating` is present on the web-vitals payloads Next forwards but is not in
// every version of the NextWebVitalsMetric type, so narrow instead of casting.
function getRating(metric: unknown) {
  if (typeof metric !== "object" || metric === null) {
    return "unknown";
  }

  const { rating } = metric as { rating?: unknown };
  return typeof rating === "string" ? rating : "unknown";
}
